import React, { useState, useEffect } from "react";
import {
  Form,
  Select,
  Checkbox,
  Button,
  Input,
  Row,
  Col,
  Card,
  Space,
  message,
} from "antd";
import { SearchOutlined, PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const { Option } = Select;

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://103.61.224.178:9876";

const PreConfirmation = () => {
  const [form] = Form.useForm();
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [memberSearchText, setMemberSearchText] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
    fetchMembers();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/event`);
      if (res.data && Array.isArray(res.data.data)) {
        setEvents(res.data.data);
      } else {
        throw new Error("Invalid events data format");
      }
    } catch (err) {
      console.error("Error fetching events:", err);
      message.error(err.message || "Failed to fetch events.");
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/member/get-all-members`);
      if (res.data && Array.isArray(res.data.data)) {
        const activeMembers = res.data.data.filter((m) => !m.isDeleted);
        setMembers(activeMembers);
        setFilteredMembers(activeMembers);
      } else {
        throw new Error("Invalid members data format");
      }
    } catch (err) {
      console.error("Error fetching members:", err);
      message.error(err.message || "Failed to fetch members.");
    }
  };

  const fetchEventPreConfirm = async (eventId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/member/geteventpreconfirm`);
      console.log("Pre-confirmation response:", res.data);

      if (Array.isArray(res.data)) {
        const records = res.data
          .filter((r) => (r.eventid?._id === eventId || r.eventid === eventId))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        const record = records[0];

        if (record) {
          setCurrentRecordId(record._id);

          const loadedMemberIds = (record.memberid || [])
            .map((m) => m._id || m)
            .filter((id) => members.some((mem) => mem._id === id));

          setSelectedMembers(loadedMemberIds);

          form.setFieldsValue({
            eventId: record.eventid._id || record.eventid,
            nonmember: record.nonmember || [],
          });

          message.info("Loaded existing pre-confirmation for this event.");
        } else {
          // No existing record: clear members and non-members, keep event selected
          setCurrentRecordId(null);
          setSelectedMembers([]);
          form.setFieldsValue({
            nonmember: [],
          });
          message.info("No pre-confirmation found. Starting fresh.");
        }
      } else {
        throw new Error("Invalid pre-confirmation data format");
      }
    } catch (err) {
      console.error("Error fetching pre-confirmation:", err);
      message.error(err.message || "Failed to load pre-confirmation data.");
      // On error, just clear member selection (keep event)
      setCurrentRecordId(null);
      setSelectedMembers([]);
      form.setFieldsValue({ nonmember: [] });
    }
  };

  // Filter members based on search
  useEffect(() => {
    const filtered = members.filter((m) =>
      m.Name.toLowerCase().includes(memberSearchText.toLowerCase())
    );
    setFilteredMembers(filtered);

    // Update Select All checkbox state
    setSelectAll(
      filtered.length > 0 &&
        filtered.every((m) => selectedMembers.includes(m._id))
    );
  }, [memberSearchText, members, selectedMembers]);

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    setSelectedMembers(checked ? filteredMembers.map((m) => m._id) : []);
  };

  const handleMemberSelect = (checked, memberId) => {
    const updated = checked
      ? [...selectedMembers, memberId]
      : selectedMembers.filter((id) => id !== memberId);

    setSelectedMembers(updated);

    // Update Select All if needed
    setSelectAll(
      filteredMembers.length > 0 &&
        updated.length === filteredMembers.length
    );
  };

  const handleEventChange = (eventId) => {
    setSelectedEvent(eventId);
    form.setFieldsValue({ eventId });

    if (members.length > 0) {
      fetchEventPreConfirm(eventId);
    } else {
      // Wait for members to load
      const interval = setInterval(() => {
        if (members.length > 0) {
          clearInterval(interval);
          fetchEventPreConfirm(eventId);
        }
      }, 100);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        eventid: values.eventId,
        memberid: selectedMembers,
        nonmember: values.nonmember || [],
      };

      console.log("Submitting payload:", payload);

      if (currentRecordId) {
        // Update existing record
        await axios.put(
          `${API_BASE_URL}/member/updateeventpreconfirm/${currentRecordId}`,
          payload
        );
        message.success("Pre-confirmation updated successfully");
      } else {
        // Create new record
        const res = await axios.post(
          `${API_BASE_URL}/member/eventpreconfirm`,
          payload
        );
        message.success("Pre-confirmation saved successfully");

        // If backend returns the new record, capture its ID
        if (res.data?.data?._id) {
          setCurrentRecordId(res.data.data._id);
        }
      }

      // Reload the latest data for this event (refreshes members & non-members)
      await fetchEventPreConfirm(values.eventId);

      // Event remains selected — no need to reset anything else

    } catch (err) {
      console.error("Error submitting pre-confirmation:", err);
      message.error(
        err.response?.data?.message || "Failed to save pre-confirmation."
      );
    }
  };

  return (
    <Row gutter={[24, 24]} style={{ padding: 16 }}>
      <Col span={24}>
        <Card
          title="Pre-Confirmation Form"
          style={{ borderRadius: 8 }}
          extra={
            <Space>
              <Button
                type="primary"
                onClick={() => navigate("/dashboard/preconlist")}
              >
                List
              </Button>
            </Space>
          }
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="eventId"
              label="Event"
              rules={[{ required: true, message: "Please select an event" }]}
            >
              <Select
                placeholder="Select Event"
                showSearch
                optionFilterProp="children"
                onChange={handleEventChange}
                value={selectedEvent}
              >
                {events.map((event) => (
                  <Option key={event._id} value={event._id}>
                    {event.eventName}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {selectedEvent && (
              <>
                <Form.Item label="Members">
                  <Input
                    placeholder="Search Members"
                    prefix={<SearchOutlined />}
                    value={memberSearchText}
                    onChange={(e) => setMemberSearchText(e.target.value)}
                    style={{ width: 250, marginBottom: 16 }}
                  />
                  <Checkbox checked={selectAll} onChange={handleSelectAll}>
                    Select All Visible
                  </Checkbox>
                  <div
                    style={{
                      maxHeight: 300,
                      overflowY: "auto",
                      marginTop: 8,
                      padding: 8,
                      border: "1px solid #f0f0f0",
                      borderRadius: 4,
                      backgroundColor: "#fafafa",
                    }}
                  >
                    {filteredMembers.length === 0 ? (
                      <div style={{ textAlign: "center", color: "#999", padding: 16 }}>
                        No members found
                      </div>
                    ) : (
                      filteredMembers.map((member) => (
                        <div
                          key={member._id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: 8,
                          }}
                        >
                          <Checkbox
                            checked={selectedMembers.includes(member._id)}
                            onChange={(e) =>
                              handleMemberSelect(e.target.checked, member._id)
                            }
                          />
                          <span style={{ marginLeft: 8 }}>{member.Name}</span>
                        </div>
                      ))
                    )}
                  </div>
                </Form.Item>

                <Form.Item label="Non-Members">
                  <Form.List name="nonmember">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...rest }) => (
                          <Space
                            key={key}
                            align="baseline"
                            style={{ display: "flex", marginBottom: 8 }}
                          >
                            <Form.Item
                              {...rest}
                              name={[name, "name"]}
                              rules={[{ required: true, message: "Enter name" }]}
                            >
                              <Input placeholder="Name" />
                            </Form.Item>
                            <Form.Item
                              {...rest}
                              name={[name, "mobilenumber"]}
                              rules={[
                                { required: true, message: "Enter mobile" },
                                { pattern: /^\d{10}$/, message: "Must be 10 digits" },
                              ]}
                            >
                              <Input placeholder="Mobile" />
                            </Form.Item>
                            <Form.Item
                              {...rest}
                              name={[name, "address"]}
                              rules={[{ required: true, message: "Enter address" }]}
                            >
                              <Input placeholder="Address" />
                            </Form.Item>
                            <MinusCircleOutlined onClick={() => remove(name)} />
                          </Space>
                        ))}
                        <Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined />}
                          >
                            Add Non-Member
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </Form.Item>
              </>
            )}

            <Form.Item>
              <Button type="primary" htmlType="submit" style={{ borderRadius: 4 }}>
                {currentRecordId ? "Update Pre-Confirmation" : "Save Pre-Confirmation"}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </Row>
  );
};

export default PreConfirmation;